import singleton from './singleton';
import { useState, useEffect, useMemo } from 'react';
import {
  callIsAuthenticated,
  callAllowsAnonymous,
  callAuthenticateEndpoint,
  callGetDil,
  callGetUsername,
  callMultiFactorAuthenticateEndpoint,
  callAuthenticateTokenEndpoint,
} from './auth';

import { UserDetails } from './types';

export enum Status {
  done,
  busy,
  actionRequired,
}

interface AuthenticationOptions {
  noAnonymous?: boolean;
  multiFactorAuthentication?: boolean;
  multiFactorCallback?: (args: any) => boolean;
}

interface useSRSInterface {
  error: any; // error object
  status: Status;
  authenticate(userName: string, password: string): void;
  multiFactorAuthenticate(pin: string): void;
  authenticateToken(token: string): Promise<UserDetails>;
  isReady: boolean; // boolean that tells you if you're authenticated and have the DIL/username
  isPartiallyAuthed: boolean; // boolean that tells you if you've completed initial login, but haven't completed multifactor auth step (only for multiFactor use case)
  isLoading: boolean; // boolean that tells you whether or not to show the spinner
}

/**
 * Hook to initialize state for the middleware
 * @param hostName The domain for the target instance of SRS. E.g. hagar.pnl.gov
 * @param path The base path for SRS routes, e.g. /srs/datachannels/DevNeoFlow_V5
 * @param options
 */
export function useSRS(hostName: string, path: string, options: AuthenticationOptions = {}): useSRSInterface {
  const { noAnonymous = false } = options;

  singleton.setPath(path);
  singleton.setHostName(hostName);
  // hook external state
  const [status, setStatus] = useState<Status>(Status.busy);
  const [error, setError] = useState<any>(null);

  // hook internal state
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [multiFactorAuthenticated, setMultiFactorAuthenticated] = useState<boolean | null>(null);
  const [allowsAnonymous, setAllowsAnonymous] = useState<boolean | null>(null);
  const [haveDil, setHaveDil] = useState<boolean>(false);
  const [haveUserName, setHaveUserName] = useState<boolean>(false);

  const isPartiallyAutheticated: boolean = useMemo(() => {
    return !!(options?.multiFactorAuthentication && authenticated && !multiFactorAuthenticated);
  }, [authenticated, multiFactorAuthenticated, options]);

  const defaultUserDetails: UserDetails = {
    sessionId: '',
    userId: '',
    loginTime: '',
    lastRequestTime: '',
    requestCount: 0,
    status: 0,
    permissions: [],
  };

  const authenticationCallback = async (userName: string, password: string): Promise<UserDetails> => {
    let userDetails: UserDetails = defaultUserDetails;
    try {
      if (!Status.busy) {
        setStatus(Status.busy);
      }
      if (error) {
        setError(null);
      }

      userDetails = await callAuthenticateEndpoint(userName, password);
      setAuthenticated(true);
    } catch (e) {
      setStatus(Status.actionRequired);
      e.fromAuthAttempt = true;
      setError(e);
    }
    return userDetails;
  };

  const tokenAuthenticationCallback = async (token: string): Promise<UserDetails> => {
    let userDetails: UserDetails = defaultUserDetails;
    try {
      !Status.busy && setStatus(Status.busy);
      error && setError(error);

      userDetails = await callAuthenticateTokenEndpoint(token);
      setAuthenticated(true);

      return userDetails;
    } catch (e) {
      setStatus(Status.actionRequired);
      setError(e);
    }
    return userDetails;
  };

  const MultiFactorAuthenticationCallback = async (...args: any[]) => {
    try {
      if (!Status.busy) {
        setStatus(Status.busy);
      }
      if (error) {
        setError(null);
      }

      // calls the default multifactor auth callback, or one provided by the application
      options.multiFactorCallback
        ? await options.multiFactorCallback(args)
        : await callMultiFactorAuthenticateEndpoint(args[0]);

      setMultiFactorAuthenticated(true);
    } catch (e) {
      setStatus(Status.actionRequired);
      e.fromAuthAttempt = true;
      setError(e);
    }
  };

  /**
   * Initial check if authenticated. This effect only runs once.
   *
   * It checks if the user is authenticated and if they are not it checks if anonymous login is valid.
   *
   * Note: Need to gather requirements to see if loggin in as anonymous can be fired at the same time as callIsAuthenticated
   */
  useEffect(() => {
    async function checkIfAuthenticated() {
      try {
        const authState = await callIsAuthenticated();
        setAuthenticated(authState);
        if (!authState) {
          const { allowsAnonymous } = await callAllowsAnonymous();
          setAllowsAnonymous(allowsAnonymous);
        }
      } catch (e) {
        setStatus(Status.actionRequired);
        setError(e);
      }
    }
    checkIfAuthenticated();
  }, []);

  /**
   * This effect runs if the user is not logged in. It handles login in as anonymous or prompts the user for action.
   * Anonymous login can also be disabled through the use of the options argument
   */

  useEffect(() => {
    if (allowsAnonymous && !noAnonymous) {
      // run anonymous things
      (async function() {
        try {
          const authState = await callAuthenticateEndpoint('anonymous_user', 'SRS');
          setAuthenticated(!!authState);
        } catch (e) {
          setError(e);
        }
      })();
    } else if (noAnonymous) {
      setStatus(Status.actionRequired);
    } else if (allowsAnonymous === false) {
      setStatus(Status.actionRequired);
    }
  }, [allowsAnonymous, noAnonymous]);

  /**
   * This effect handles fetching the DIL and username once authenticated
   * Both fetches are run in parallel and set different peices of state
   */
  useEffect(() => {
    const canFetchAuthedResources = options.multiFactorAuthentication ? multiFactorAuthenticated : authenticated;

    if (canFetchAuthedResources) {
      // get the dil
      (async function() {
        try {
          const dil = await callGetDil();
          // Destructure Dil
          singleton.setDil(dil);
          setHaveDil(true);
        } catch (e) {
          setError(e);
        }
      })();
      (async function() {
        try {
          const userName = await callGetUsername();
          singleton.setUserDetails(userName);
          setHaveUserName(true);
        } catch (e) {
          setError(e);
        }
      })();
    }
  }, [authenticated, multiFactorAuthenticated, options.multiFactorAuthentication]);

  /**
   * This effect listens for changes to the pieces of state that make up all parts of the process
   * Once all three pieces of state are true then we are done with the authentication process
   */
  useEffect(() => {
    if (authenticated && haveDil && haveUserName) {
      setStatus(Status.done);
      singleton.setUnauthenticatedCallback(() => {
        setAuthenticated(false);
        setError(null);
        setStatus(Status.actionRequired);
        singleton.setUnauthenticatedCallback(undefined);
      });
    }
  }, [authenticated, haveDil, haveUserName]);

  return {
    error,
    status: status,
    authenticate: authenticationCallback,
    multiFactorAuthenticate: MultiFactorAuthenticationCallback,
    authenticateToken: tokenAuthenticationCallback,
    isReady: status === Status.done,
    isPartiallyAuthed: isPartiallyAutheticated,
    isLoading: status === Status.busy,
  };
}
